import { type FC } from 'react';

import {
  Center,
  type CenterProps,
  Icon as ChakraIcon,
  type IconProps as ChakraIconProps,
  type ComponentWithAs,
  Flex,
  type FlexProps,
  Heading,
} from '@chakra-ui/react';

type TitlePositions = 'top' | 'bottom' | 'left' | 'right';

type SquareIconContainerProps = Omit<CenterProps, 'size'> & {
  /** The icon component to display */
  icon:
    | ComponentWithAs<'svg', ChakraIconProps>
    | FC<React.PropsWithChildren<unknown>>;
  /** Size of the container in pixels (default: 48) */
  size?: number;
  /** Size of the icon in pixels (default: auto-calculated as 40% of container size) */
  iconSize?: number;
  /** Background color of the container (default: 'brand.50') */
  backgroundColor?: string;
  /** Icon color (default: inherits from theme) */
  iconColor?: string;
  /** Border color (default: 'brand.25') */
  borderColor?: string;
  /** Border width (default: 4) */
  borderWidth?: number;
  /** Border radius (default: '2xl') */
  borderRadius?: string;
  /** Enable/disable border (default: true) */
  showBorder?: boolean;
  /** Optional title text */
  title?: string;
  /** Title font size (default: 'lg') */
  titleSize?: string;
  /** Title font weight (default: 'bold') */
  titleWeight?: string | number;
  /** Position of title relative to icon (default: 'right') */
  titlePosition?: TitlePositions;
  /** Gap between icon and title (default: 4) */
  gap?: string | number;
  /** Width of the entire component */
  w?: string | number;
};

const getFlexDirection = (position: TitlePositions): FlexProps['direction'] => {
  switch (position) {
    case 'top':
      return 'column-reverse';
    case 'bottom':
      return 'column';
    case 'left':
      return 'row-reverse';
    case 'right':
    default:
      return 'row';
  }
};

export const SquareIconContainer: React.FC<SquareIconContainerProps> = ({
  icon: IconComponent,
  size = 48,
  iconSize,
  backgroundColor,
  iconColor,
  borderColor = 'brand.25',
  borderWidth = 4,
  borderRadius = '2xl',
  showBorder = true,
  title,
  titleSize = 'lg',
  titleWeight = 'bold',
  titlePosition = 'right',
  gap = 4,
  w,
  ...props
}) => {
  // Support both new and old prop names for backward compatibility
  const finalBackgroundColor = backgroundColor ?? 'brand.50';
  const finalIconColor = iconColor ?? 'brand.500';

  // Calculate icon size as 50% of container if not provided
  const calculatedIconSize = iconSize ?? `${Math.round(size * 0.4)}px`;

  const flexDirection = getFlexDirection(titlePosition);

  const iconContainer = (
    <Center
      borderWidth={showBorder ? borderWidth : 0}
      borderRadius={borderRadius}
      borderColor={borderColor}
      h={`${size}px`}
      w={`${size}px`}
      bgColor={finalBackgroundColor}
      {...props}
    >
      <ChakraIcon
        as={IconComponent}
        fontSize={calculatedIconSize}
        color={finalIconColor}
      />
    </Center>
  );

  return (
    <Flex
      gap={gap}
      direction={flexDirection}
      justifyItems="center"
      alignItems="center"
      minW={0}
      w={w}
    >
      {iconContainer}
      {title && (
        <Heading
          fontSize={titleSize}
          noOfLines={1}
          overflowWrap="break-word"
          fontWeight={titleWeight}
        >
          {title}
        </Heading>
      )}
    </Flex>
  );
};
